import type {
    DragEndEvent,
    DragMoveEvent,
    DragStartEvent,
} from '@dnd-kit/core';
import {
    type DeepPartial,
    type MoneyMapRuleGroup as MoneyMapRuleGroupType,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
    type RuleOrGroup,
} from '@fruitful/types';
import { useCallback, useMemo, useRef, useState } from 'react';

import type { MembershipDetails } from '../../../entities/entityTypes';
import { type GoalPanelPreset } from '../../utils/buildGoalPanelPresetGroupDto';
import {
    computeGoalPanelPresetPreviewInsertIndex,
    createGoalPanelPresetCollisionDetection,
    getPresetFromDragEvent,
    handleGoalPanelPresetDrop,
} from './goalPanelPresetDrop';
import {
    type GoalsWaterfallDnDControls,
    useGoalsWaterfallDnD,
} from './useGoalsWaterfallDnD';

interface UseMoneyMapperGoalsDnDParams {
    goalRules: Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    goalItems: RuleOrGroup[];
    groups: Array<DeepPartial<MoneyMapRuleGroupType>>;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    membership: MembershipDetails | null;
    monthlyExpensesCents: number;
    nonGoalRuleCount: number;
    onUpdateMoneyMap: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<boolean>;
    onDeleteGroup: (groupId: string) => Promise<void>;
    onDeleteGoal?: (ruleId: string) => Promise<void>;
    refetchMoneyMap?: () => Promise<unknown>;
    getLatestMoneyMapGroups?: () => Array<DeepPartial<MoneyMapRuleGroupType>>;
}

export interface MoneyMapperGoalsDnDControls {
    goalsWaterfallDnd: GoalsWaterfallDnDControls;
    activePreset: GoalPanelPreset | null;
    presetPreviewInsertIndex: number | null;
    presetPreviewPreset: GoalPanelPreset | null;
    onDragStart: (event: DragStartEvent) => void;
    onDragMove: (event: DragMoveEvent) => void;
    onDragEnd: (event: DragEndEvent) => Promise<void>;
    onDragCancel: () => void;
    collisionDetectionStrategy: ReturnType<
        typeof createGoalPanelPresetCollisionDetection
    >;
}

/**
 * Composes waterfall tile DnD with add-a-goal panel preset drags (insert preview,
 * merge-into-group, and persistence).
 */
export const useMoneyMapperGoalsDnD = ({
    goalRules,
    goalItems,
    groups,
    moneyMap,
    membership,
    monthlyExpensesCents,
    nonGoalRuleCount,
    onUpdateMoneyMap,
    onDeleteGroup,
    onDeleteGoal,
    refetchMoneyMap,
    getLatestMoneyMapGroups,
}: UseMoneyMapperGoalsDnDParams): MoneyMapperGoalsDnDControls => {
    const topLevelIds = useMemo(
        () => goalItems.map((item) => item.id ?? ''),
        [goalItems],
    );

    const getGroupRules = useCallback(
        (groupId: string | undefined) =>
            goalRules.filter((r) => r.moneyMapRuleGroupId === groupId),
        [goalRules],
    );

    const goalsWaterfallDnd = useGoalsWaterfallDnD({
        goalRules,
        goalItems,
        groups,
        topLevelIds,
        moneyMap,
        onUpdateMoneyMap,
        onDeleteGroup,
        onDeleteGoal,
        refetchMoneyMap,
        getGroupRules,
        getLatestMoneyMapGroups,
    });

    const [activePreset, setActivePreset] = useState<GoalPanelPreset | null>(
        null,
    );
    const [presetPreviewInsertIndex, setPresetPreviewInsertIndex] = useState<
        number | null
    >(null);
    const dragStartPointerY = useRef(0);

    const clearPresetDragState = useCallback(() => {
        setActivePreset(null);
        setPresetPreviewInsertIndex(null);
    }, []);

    const collisionDetectionStrategy = useMemo(
        () =>
            createGoalPanelPresetCollisionDetection(
                goalsWaterfallDnd.collisionDetectionStrategy,
            ),
        [goalsWaterfallDnd.collisionDetectionStrategy],
    );

    const onDragStart = useCallback(
        (event: DragStartEvent) => {
            const preset = getPresetFromDragEvent(event);
            if (preset != null) {
                setActivePreset(preset);
                if (event.activatorEvent instanceof PointerEvent) {
                    dragStartPointerY.current = event.activatorEvent.clientY;
                }
                goalsWaterfallDnd.handlePresetDragStart(event);
                return;
            }
            goalsWaterfallDnd.handleDragStart(event);
        },
        [goalsWaterfallDnd],
    );

    const onDragMove = useCallback(
        (event: DragMoveEvent) => {
            goalsWaterfallDnd.handleDragMove(event);

            const preset = getPresetFromDragEvent(event);
            if (preset == null) {
                setPresetPreviewInsertIndex(null);
                return;
            }

            setPresetPreviewInsertIndex(
                computeGoalPanelPresetPreviewInsertIndex({
                    event,
                    dropIndicator: goalsWaterfallDnd.getDropIndicator(),
                    goalItemCount: goalItems.length,
                }),
            );
        },
        [goalItems.length, goalsWaterfallDnd],
    );

    const onDragEnd = useCallback(
        async (event: DragEndEvent) => {
            const preset = getPresetFromDragEvent(event);
            if (preset != null) {
                const dropIndicator = goalsWaterfallDnd.getDropIndicator();
                const previewInsertIndex = presetPreviewInsertIndex;
                clearPresetDragState();
                goalsWaterfallDnd.handleDragCancel();

                await handleGoalPanelPresetDrop({
                    event,
                    dropIndicator,
                    previewInsertIndex,
                    dragStartPointerY: dragStartPointerY.current,
                    membership,
                    fetchedMoneyMap: moneyMap,
                    goalItems,
                    nonGoalRuleCount,
                    monthlyExpensesCents,
                    getGroupRules,
                    onUpdateMoneyMap,
                });
                return;
            }

            await goalsWaterfallDnd.handleDragEnd(event);
            clearPresetDragState();
        },
        [
            clearPresetDragState,
            getGroupRules,
            goalItems,
            goalsWaterfallDnd,
            membership,
            moneyMap,
            monthlyExpensesCents,
            nonGoalRuleCount,
            onUpdateMoneyMap,
            presetPreviewInsertIndex,
        ],
    );

    const onDragCancel = useCallback(() => {
        clearPresetDragState();
        goalsWaterfallDnd.handleDragCancel();
    }, [clearPresetDragState, goalsWaterfallDnd]);

    const presetPreviewPreset =
        presetPreviewInsertIndex != null ? activePreset : null;

    return {
        goalsWaterfallDnd,
        activePreset,
        presetPreviewInsertIndex,
        presetPreviewPreset,
        onDragStart,
        onDragMove,
        onDragEnd,
        onDragCancel,
        collisionDetectionStrategy,
    };
};
